import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaButton, PaziaLoader, PaziaQuill } from "components/atoms";
import { useToaster } from "utils";
import {
  CourseGroupAttributeApi,
  CourseGroupAttributeType,
  CourseGroupAttributeValueApi,
  coursegroupAttributesApi,
  useCourseGroupAttributeValues,
  useCourseGroupAttributesAll,
} from "api/coursegroup-attributes";

interface CourseGroupAttributesTabProps {
  courseGroupId: string;
}

interface AttributeRow {
  courseGroupAttributeId: number;
  attributeName: string;
  attributeType: CourseGroupAttributeType;
  boolValue: boolean;
  textValue: string;
  selectedOptionIds: string[];
  availableOptions: { value: string; label: string; color: string | null }[];
}

const ColorDot = ({ color }: { color: string | null }) => {
  if (!color) return null;
  return (
    <span
      className="mr-1.5 inline-block h-3 w-3 rounded-full border border-slate-300"
      style={{ backgroundColor: color }}
    />
  );
};

export const CourseGroupAttributesTab = ({ courseGroupId }: CourseGroupAttributesTabProps) => {
  const { isLoading: loadingValues, values, invalidate } = useCourseGroupAttributeValues(courseGroupId);
  const { isLoading: loadingAll, allAttributes } = useCourseGroupAttributesAll();
  const { showSuccess, showError } = useToaster();
  const [rows, setRows] = useState<AttributeRow[]>([]);
  const [saving, setSaving] = useState(false);
  const initializedRef = useRef(false);

  const valuesJson = JSON.stringify(values.map((v) => v.id));

  useEffect(() => {
    if (values.length > 0 || (!loadingValues && !initializedRef.current)) {
      initializedRef.current = true;
      setRows(
        values.map((v) => ({
          courseGroupAttributeId: v.courseGroupAttributeId,
          attributeName: v.attributeName,
          attributeType: v.attributeType,
          boolValue: v.boolValue ?? false,
          textValue: v.textValue ?? "",
          selectedOptionIds: v.selections.map((s) => s.optionId.toString()),
          availableOptions: v.availableOptions.map((o) => ({
            value: o.id.toString(),
            label: o.value,
            color: o.color,
          })),
        })),
      );
    }
  }, [valuesJson, loadingValues]);

  const assignedIds = useMemo(() => new Set(rows.map((r) => r.courseGroupAttributeId)), [rows]);

  const unassignedAttributes = useMemo(
    () => allAttributes.filter((a) => !assignedIds.has(a.id)),
    [allAttributes, assignedIds],
  );

  const addAttribute = (attr: CourseGroupAttributeApi) => {
    setRows((prev) => [
      ...prev,
      {
        courseGroupAttributeId: attr.id,
        attributeName: attr.name,
        attributeType: attr.attributeType,
        boolValue: false,
        textValue: "",
        selectedOptionIds: [],
        availableOptions: attr.options.map((o) => ({
          value: o.id.toString(),
          label: o.value,
          color: o.color,
        })),
      },
    ]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, partial: Partial<AttributeRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...partial } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await coursegroupAttributesApi.saveValues(Number(courseGroupId), {
        values: rows.map((r) => ({
          courseGroupAttributeId: r.courseGroupAttributeId,
          boolValue: r.attributeType === CourseGroupAttributeType.Boolean ? r.boolValue : null,
          textValue:
            r.attributeType === CourseGroupAttributeType.Text ||
            r.attributeType === CourseGroupAttributeType.Textarea ||
            r.attributeType === CourseGroupAttributeType.Wysiwyg
              ? r.textValue
              : null,
          selectedOptionIds:
            r.attributeType === CourseGroupAttributeType.DictionarySingleSelect ||
            r.attributeType === CourseGroupAttributeType.DictionaryMultiSelect
              ? r.selectedOptionIds.map(Number)
              : [],
        })),
      });
      showSuccess("Zapisano atrybuty");
      invalidate();
    } catch {
      showError("Nie udało się zapisać atrybutów");
    } finally {
      setSaving(false);
    }
  };

  if (loadingValues || loadingAll) return <PaziaLoader position="center" />;

  return (
    <div className="space-y-4">
      {rows.map((row, index) => (
        <AttributeRowComponent
          key={`${row.courseGroupAttributeId}-${index}`}
          row={row}
          onUpdate={(partial) => updateRow(index, partial)}
          onRemove={() => removeRow(index)}
        />
      ))}

      {unassignedAttributes.length > 0 && (
        <div className="flex items-center gap-2">
          <AddAttributeDropdown attributes={unassignedAttributes} onSelect={addAttribute} />
        </div>
      )}

      <div className="pt-4">
        <PaziaButton title={saving ? "Zapisywanie..." : "Zapisz"} type="success" onClick={handleSave} />
      </div>
    </div>
  );
};

interface AttributeRowComponentProps {
  row: AttributeRow;
  onUpdate: (partial: Partial<AttributeRow>) => void;
  onRemove: () => void;
}

const AttributeRowComponent = ({ row, onUpdate, onRemove }: AttributeRowComponentProps) => {
  const methods = useForm({
    defaultValues: {
      boolValue: row.boolValue,
      textValue: row.textValue,
      selectedOptionId: row.selectedOptionIds[0] ?? "",
      selectedOptionIds: row.selectedOptionIds,
    },
  });

  return (
    <FormProvider {...methods}>
      <div className="flex items-start gap-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
        <div className="flex-1">
          <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">{row.attributeName}</h4>

          {row.attributeType === CourseGroupAttributeType.Boolean && (
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={row.boolValue}
                onChange={(e) => onUpdate({ boolValue: e.target.checked })}
              />
              <span className="text-sm text-slate-600 dark:text-slate-300">Tak</span>
            </label>
          )}

          {row.attributeType === CourseGroupAttributeType.Text && (
            <input
              type="text"
              className="form-input w-full"
              value={row.textValue}
              onChange={(e) => onUpdate({ textValue: e.target.value })}
            />
          )}

          {row.attributeType === CourseGroupAttributeType.Textarea && (
            <textarea
              className="form-textarea w-full"
              rows={3}
              value={row.textValue}
              onChange={(e) => onUpdate({ textValue: e.target.value })}
            />
          )}

          {row.attributeType === CourseGroupAttributeType.Wysiwyg && (
            <PaziaQuill value={row.textValue} onChange={(html) => onUpdate({ textValue: html })} />
          )}

          {row.attributeType === CourseGroupAttributeType.DictionarySingleSelect && (
            <div className="flex items-center gap-2">
              <ColorDot
                color={row.availableOptions.find((o) => o.value === (row.selectedOptionIds[0] ?? ""))?.color ?? null}
              />
              <select
                className="form-select w-full"
                value={row.selectedOptionIds[0] ?? ""}
                onChange={(e) => onUpdate({ selectedOptionIds: e.target.value ? [e.target.value] : [] })}
              >
                <option value="">-- Wybierz --</option>
                {row.availableOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {row.attributeType === CourseGroupAttributeType.DictionaryMultiSelect && (
            <MultiSelectControl
              value={row.selectedOptionIds}
              options={row.availableOptions}
              onChange={(ids) => onUpdate({ selectedOptionIds: ids })}
            />
          )}
        </div>
        <div className="pt-6">
          <PaziaButton title="Usuń" type="danger" onClick={onRemove} />
        </div>
      </div>
    </FormProvider>
  );
};

interface MultiSelectControlProps {
  value: string[];
  options: { value: string; label: string; color: string | null }[];
  onChange: (ids: string[]) => void;
}

const MultiSelectControl = ({ value, options, onChange }: MultiSelectControlProps) => {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const checked = value.includes(opt.value);
        return (
          <label
            key={opt.value}
            className="flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-2 py-1 dark:border-slate-600"
          >
            <input
              type="checkbox"
              className="form-checkbox"
              checked={checked}
              onChange={() => {
                if (checked) {
                  onChange(value.filter((v) => v !== opt.value));
                } else {
                  onChange([...value, opt.value]);
                }
              }}
            />
            <ColorDot color={opt.color} />
            <span className="text-sm">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
};

interface AddAttributeDropdownProps {
  attributes: CourseGroupAttributeApi[];
  onSelect: (attr: CourseGroupAttributeApi) => void;
}

const AddAttributeDropdown = ({ attributes, onSelect }: AddAttributeDropdownProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <PaziaButton title="Dodaj atrybut +" type="secondary" onClick={() => setOpen(!open)} />
      {open && (
        <div className="absolute left-0 top-full z-10 mt-1 max-h-60 w-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {attributes.map((attr) => (
            <button
              key={attr.id}
              type="button"
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={() => {
                onSelect(attr);
                setOpen(false);
              }}
            >
              {attr.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
